import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LogIn, LogOut, Loader2, Coffee, Maximize2 } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

export default function QuickClockWidget({ expandable }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const data = await apiRequest('/kiosk/my-status');
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch clock status');
    } finally {
      setLoading(false);
    }
  };

  const handleClockAction = async (action) => {
    setClocking(true);
    try {
      await apiRequest(`/kiosk/${action}`, { method: 'POST' });
      toast.success(`${action === 'clock-in' ? 'Clocked in' : action === 'clock-out' ? 'Clocked out' : 'Break started'} successfully`);
      fetchStatus();
    } catch (error) {
      toast.error(error.message || `Failed to ${action}`);
    } finally {
      setClocking(false);
    }
  };

  return (
    <Card data-testid="quick-clock-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-lg">Time Clock</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate('/time-clock')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Status:</span>
              <Badge variant={status?.clocked_in ? 'default' : 'secondary'}>
                {status?.clocked_in ? 'Clocked In' : 'Clocked Out'}
              </Badge>
            </div>
            
            {status?.clocked_in && status?.clock_in_time && (
              <p className="text-xs text-slate-500">
                Since: {new Date(status.clock_in_time).toLocaleTimeString()}
              </p>
            )}

            <div className="flex gap-2">
              {!status?.clocked_in ? (
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700" 
                  onClick={() => handleClockAction('clock-in')}
                  disabled={clocking}
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Clock In
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleClockAction('break')}
                    disabled={clocking}
                  >
                    <Coffee className="h-4 w-4 mr-2" />
                    Break
                  </Button>
                  <Button 
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={() => handleClockAction('clock-out')}
                    disabled={clocking}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Clock Out
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
